import React, { useState, useRef, useEffect } from 'react';
import { Search, ChevronDown, X, Check, Sparkles } from 'lucide-react';
import { STAGES, SECTORS } from '../data/mockStartups';

const Dropdown = ({ label, options, value, onChange, multi = false }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const ref = useRef(null);
  const btnRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const openMenu = () => {
    if (open) { setOpen(false); return; }
    const r = btnRef.current.getBoundingClientRect();
    const width = 224;
    setPos({ top: r.bottom + 6, left: Math.max(8, Math.min(r.left, window.innerWidth - width - 8)) });
    setOpen(true);
  };

  const display = multi
    ? value.length
      ? `${label} (${value.length})`
      : label
    : value
    ? options.find((o) => o.id === value)?.label || label
    : label;

  const toggle = (id) => {
    if (multi) {
      onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
    } else {
      onChange(value === id ? null : id);
      setOpen(false);
    }
  };

  return (
    <div ref={ref} className="relative flex-shrink-0">
      <button
        ref={btnRef}
        onClick={openMenu}
        className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[13px] font-medium border transition-colors whitespace-nowrap ${
          (multi && value.length) || (!multi && value)
            ? 'bg-slate-900 text-white border-slate-900'
            : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
        }`}
      >
        {display}
        <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && pos && (
        <div
          style={{ position: 'fixed', top: pos.top, left: pos.left }}
          className="w-56 bg-white rounded-xl shadow-xl border border-slate-200 py-1.5 z-[1200] max-h-[60vh] overflow-y-auto"
        >
          {options.map((opt) => {
            const selected = multi ? value.includes(opt.id) : value === opt.id;
            return (
              <button
                key={opt.id}
                onClick={() => toggle(opt.id)}
                className="flex items-center justify-between w-full px-3 py-2 text-[13px] hover:bg-slate-50 text-slate-700"
              >
                <span>{opt.label}</span>
                {selected && <Check size={14} className="text-emerald-600" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const SortDropdown = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const ref = useRef(null);
  const btnRef = useRef(null);
  const options = [
    { id: 'name', label: 'Name' },
    { id: 'stage', label: 'Stage' },
    { id: 'sector', label: 'Sector' },
    { id: 'neighborhood', label: 'Neighborhood' },
  ];

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const openMenu = () => {
    if (open) { setOpen(false); return; }
    const r = btnRef.current.getBoundingClientRect();
    const width = 176;
    setPos({ top: r.bottom + 6, left: Math.max(8, Math.min(r.left, window.innerWidth - width - 8)) });
    setOpen(true);
  };

  const current = options.find((o) => o.id === value)?.label || 'Name';

  return (
    <div ref={ref} className="relative flex-shrink-0">
      <button
        ref={btnRef}
        onClick={openMenu}
        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[13px] font-medium border bg-white text-slate-700 border-slate-200 hover:border-slate-300 whitespace-nowrap"
      >
        Sort: {current}
        <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && pos && (
        <div
          style={{ position: 'fixed', top: pos.top, left: pos.left }}
          className="w-44 bg-white rounded-xl shadow-xl border border-slate-200 py-1.5 z-[1200]"
        >
          {options.map((opt) => (
            <button
              key={opt.id}
              onClick={() => {
                onChange(opt.id);
                setOpen(false);
              }}
              className="flex items-center justify-between w-full px-3 py-2 text-[13px] hover:bg-slate-50 text-slate-700"
            >
              <span>{opt.label}</span>
              {value === opt.id && <Check size={14} className="text-emerald-600" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const FiltersBar = ({
  search,
  setSearch,
  stages,
  setStages,
  sectors,
  setSectors,
  eventsOnly,
  setEventsOnly,
  ycOnly,
  setYcOnly,
  accelOnly,
  setAccelOnly,
  sort,
  setSort,
  clusterMode,
  setClusterMode,
  onAskAi,
  inline = false,
}) => {
  const searchField = (
    <>
      <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        data-testid="filters-search-input"
        placeholder="Search startups, sectors, founders…"
        className="w-full pl-9 pr-9 py-2.5 text-[13px] bg-white border border-slate-200 rounded-full shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-300 text-slate-800 placeholder:text-slate-400"
      />
      {search && (
        <button
          onClick={() => setSearch('')}
          aria-label="Clear search"
          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
        >
          <X size={14} />
        </button>
      )}
    </>
  );

  const pills = (
    <>
      {onAskAi && (
        <button
          onClick={onAskAi}
          data-testid={inline ? 'mobile-filters-ask-ai-button' : 'filters-ask-ai-button'}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[13px] font-semibold border bg-slate-900 text-white border-slate-900 hover:bg-slate-800 whitespace-nowrap flex-shrink-0 shadow-sm"
        >
          <Sparkles size={14} className="text-amber-300" />
          Ask Turoczy AI
        </button>
      )}
      <Dropdown
        label="Stage"
        options={STAGES}
        value={stages}
        onChange={setStages}
        multi
      />
      <Dropdown
        label="Sector"
        options={SECTORS.map((s) => ({ id: s, label: s }))}
        value={sectors}
        onChange={setSectors}
        multi
      />

      <button
        onClick={() => setEventsOnly(!eventsOnly)}
        className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[13px] font-medium border whitespace-nowrap transition-colors ${
          eventsOnly
            ? 'bg-slate-900 text-white border-slate-900'
            : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
        }`}
      >
        Events
      </button>

      <button
        onClick={() => setYcOnly(!ycOnly)}
        className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[13px] font-medium border whitespace-nowrap transition-colors ${
          ycOnly
            ? 'bg-orange-500 text-white border-orange-500'
            : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
        }`}
      >
        YC
      </button>

      <SortDropdown value={sort} onChange={setSort} />

      <button
        onClick={() => setClusterMode(!clusterMode)}
        title="Group startups by neighborhood"
        className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[13px] font-medium border whitespace-nowrap transition-colors ${
          clusterMode
            ? 'bg-emerald-600 text-white border-emerald-600'
            : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
        }`}
      >
        Neighborhoods
      </button>
    </>
  );

  if (inline) {
    return (
      <div className="w-full" data-testid="filters-inline">
        <div className="relative w-full">{searchField}</div>
        <div
          className="mt-3 flex gap-2 items-center overflow-x-auto scrollbar-hide pb-1"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {pills}
        </div>
      </div>
    );
  }

  return (
    <div className="hidden sm:block absolute top-16 left-4 right-4 z-[900] pointer-events-none">
      <div className="relative w-full sm:w-72 pointer-events-auto">{searchField}</div>

      <div
        className="mt-2 flex gap-2 items-center overflow-x-auto sm:flex-wrap scrollbar-hide pointer-events-auto pb-1"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {pills}
      </div>
    </div>
  );
};

export default FiltersBar;
